import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    DateInput,
    FOCUS_RING,
    Icon,
    Label,
    ModalDialog,
    Text,
    formatDate,
    DATE_FORMATS,
} from '@amelio/ui-library';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EvaluationPeriodOption } from '../../Evaluation/types';
import type { FormEvent } from 'react';

import { Locale } from '@/constants/Locale';
import i18n from '@/plugins/i18n';

const toISOMidnightUTC = (value?: string | Date): string => {
    if (!value) return '';
    const isoDate = formatDate({
        date: value,
        format: DATE_FORMATS.ISO_DATE,
        language: i18n.language,
    });
    return `${isoDate}T00:00:00.000Z`;
};

interface PeriodTileProps {
    label: string;
    description?: string;
    isSelected: boolean;
    disabled: boolean;
    onClick: () => void;
}

/** A selectable period card: a toggle button whose pressed state is the selection. */
const PeriodTile = ({ label, description, isSelected, disabled, onClick }: PeriodTileProps) => (
    <button
        type={'button'}
        aria-pressed={isSelected}
        disabled={disabled}
        onClick={onClick}
        className={`${FOCUS_RING} flex cursor-pointer flex-col gap-1 border-2 p-4 text-left shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            isSelected
                ? 'border-primary bg-primary-50'
                : 'border-base-100 hover:border-base-200 bg-white'
        }`}
    >
        <Text
            as={'span'}
            weight={'medium'}
            color={Colors.BASE_600}
        >
            {label}
        </Text>

        {description && (
            <Text
                as={'span'}
                size={'small'}
                color={Colors.BASE_500}
            >
                {description}
            </Text>
        )}
    </button>
);

interface HandoutEvalPeriodModalProps {
    open: boolean;
    onOpenChange: (v: boolean) => void;
    selectedPeriodId: string;
    onPeriodSave: (periodId: string, startDate: string, endDate: string) => void;
    customFromDate?: Date;
    customToDate?: Date;
    periods: EvaluationPeriodOption[];
    disabled?: boolean;
}

export const CUSTOM_PERIOD_VALUE = 'custom';

export const HandoutEvalPeriodModal = ({
    open,
    onOpenChange,
    selectedPeriodId,
    onPeriodSave,
    customFromDate,
    customToDate,
    periods,
    disabled = false,
}: HandoutEvalPeriodModalProps) => {
    const { t } = useTranslation();
    const lang = i18n.language === Locale.EN ? 'en' : 'fr';
    const fromDateId = useId();
    const toDateId = useId();

    const [localSelectedPeriod, setLocalSelectedPeriod] = useState(selectedPeriodId);
    const [localShowCustom, setLocalShowCustom] = useState(
        selectedPeriodId === CUSTOM_PERIOD_VALUE,
    );
    const [localFromDate, setLocalFromDate] = useState(customFromDate);
    const [localToDate, setLocalToDate] = useState(customToDate);

    const isCustomPeriodInvalid = useMemo(
        () =>
            localShowCustom &&
            localFromDate !== undefined &&
            localToDate !== undefined &&
            localToDate.getTime() < localFromDate.getTime(),
        [localShowCustom, localFromDate, localToDate],
    );

    useEffect(() => {
        if (open) {
            // Only pre-select a period if dates are actually available
            const hasDates = customFromDate || customToDate;
            setLocalSelectedPeriod(hasDates ? selectedPeriodId : '');
            setLocalShowCustom(Boolean(hasDates && selectedPeriodId === CUSTOM_PERIOD_VALUE));
            // Only set dates if they have actual values; don't pre-populate with empty/undefined
            setLocalFromDate(customFromDate || undefined);
            setLocalToDate(customToDate || undefined);
        }
    }, [open, selectedPeriodId, customFromDate, customToDate]);

    const handlePeriodSelect = (periodId: string) => {
        setLocalSelectedPeriod(periodId);
        setLocalShowCustom(false);

        const predefined = periods.find((p) => p.value === periodId || p.id === periodId);
        if (predefined) {
            const startDate = predefined.startDate || predefined.from;
            const endDate = predefined.endDate || predefined.to;
            setLocalFromDate(startDate ? new Date(startDate) : undefined);
            setLocalToDate(endDate ? new Date(endDate) : undefined);
        }
    };

    const handleCustomToggle = () => {
        if (!localShowCustom) {
            setLocalShowCustom(true);
            setLocalSelectedPeriod(CUSTOM_PERIOD_VALUE);
        } else {
            setLocalShowCustom(false);
            setLocalSelectedPeriod('');
        }
    };

    const canSave = localShowCustom
        ? Boolean(localFromDate && localToDate && localToDate.getTime() >= localFromDate.getTime())
        : Boolean(localSelectedPeriod);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSave || disabled) {
            return;
        }

        let startDate: Date | string | undefined = localFromDate;
        let endDate: Date | string | undefined = localToDate;

        if (!localShowCustom) {
            const predefined = periods.find(
                (p) => p.value === localSelectedPeriod || p.id === localSelectedPeriod,
            );
            if (predefined) {
                startDate = predefined.startDate || predefined.from;
                endDate = predefined.endDate || predefined.to;
            }
        }

        if (!startDate || !endDate || isCustomPeriodInvalid) {
            return;
        }

        onPeriodSave(localSelectedPeriod, toISOMidnightUTC(startDate), toISOMidnightUTC(endDate));
        onOpenChange(false);
    };

    const handleClose = () => onOpenChange(false);

    return (
        <ModalDialog
            isOpen={open}
            onClose={handleClose}
            title={t('managerAssessmentEvaluation.evaluationPeriod')}
            subtitle={t('postReviewPrepareMeeting.handout.selectEvaluationPeriod')}
            size={'md'}
            closeAriaLabel={t('common.close')}
            onSubmit={handleSubmit}
            backdropBlur
            dataTestId={'handout-eval-period-modal'}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'sm'}
                        onClick={handleClose}
                        disabled={disabled}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        size={'sm'}
                        disabled={!canSave || disabled}
                    >
                        {t('common.save')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-6'}>
                <div className={'grid grid-cols-2 gap-3'}>
                    {periods.map((period) => (
                        <PeriodTile
                            key={period.value || period.id}
                            label={period.label}
                            description={period.description}
                            isSelected={
                                localSelectedPeriod === period.value ||
                                localSelectedPeriod === period.id
                            }
                            disabled={disabled}
                            onClick={() => handlePeriodSelect(period.value || period.id || '')}
                        />
                    ))}
                </div>

                <PeriodTile
                    label={t('managerAssessmentEvaluation.customPeriodOption')}
                    description={t('postReviewPrepareMeeting.handout.defineCustomDates')}
                    isSelected={localShowCustom}
                    disabled={disabled}
                    onClick={handleCustomToggle}
                />

                {localShowCustom && (
                    <div className={'bg-base-25 flex flex-col gap-4 rounded-lg p-4'}>
                        <div className={'flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4'}>
                            <div
                                className={
                                    'ds-gap-sm flex flex-1 flex-col sm:flex-row sm:items-center sm:gap-[12px]'
                                }
                            >
                                <Label htmlFor={fromDateId}>
                                    {t('managerAssessmentEvaluation.periodFrom')}
                                </Label>

                                <div className={'flex-1'}>
                                    <DateInput
                                        id={fromDateId}
                                        ariaLabel={t('managerAssessmentEvaluation.periodFrom')}
                                        value={localFromDate}
                                        onChange={(date) => setLocalFromDate(date)}
                                        lang={lang}
                                        placeholder={t('managerAssessmentEvaluation.periodFrom')}
                                        clearable={false}
                                        disabled={disabled}
                                        dataTestId={'handout-period-from'}
                                        variant={'violet'}
                                        iconPlacement={'leading'}
                                        idleBorderClassName={'border-slate-200'}
                                        inputClassName={'rounded-[12px]'}
                                    />
                                </div>
                            </div>

                            <span
                                className={'text-base-300 hidden sm:block'}
                                aria-hidden={'true'}
                            >
                                →
                            </span>

                            <div
                                className={
                                    'ds-gap-sm flex flex-1 flex-col sm:flex-row sm:items-center sm:gap-[12px]'
                                }
                            >
                                <Label htmlFor={toDateId}>
                                    {t('managerAssessmentEvaluation.periodTo')}
                                </Label>

                                <div className={'flex-1'}>
                                    <DateInput
                                        id={toDateId}
                                        ariaLabel={t('managerAssessmentEvaluation.periodTo')}
                                        value={localToDate}
                                        onChange={(date) => setLocalToDate(date)}
                                        lang={lang}
                                        placeholder={t('managerAssessmentEvaluation.periodTo')}
                                        clearable={false}
                                        disabled={disabled}
                                        disabledDates={
                                            localFromDate ? { before: localFromDate } : undefined
                                        }
                                        dataTestId={'handout-period-to'}
                                        variant={'violet'}
                                        iconPlacement={'leading'}
                                        idleBorderClassName={'border-slate-200'}
                                        inputClassName={'rounded-[12px]'}
                                    />
                                </div>
                            </div>
                        </div>

                        {isCustomPeriodInvalid && (
                            <div
                                className={'ds-gap-xs flex items-center'}
                                role={'alert'}
                                data-testid={'handout-custom-period-error'}
                            >
                                <Icon
                                    name={'essential-danger'}
                                    size={16}
                                    color={Colors.ERROR}
                                    ariaHidden
                                />

                                <Text
                                    as={'span'}
                                    size={'small'}
                                    color={Colors.ERROR}
                                >
                                    {t('managerAssessmentEvaluation.customPeriodInvalid')}
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};
